import "zod/compile";
import { z } from "zod";

const identity = { id: z.string().min(1).max(80) };
export const surplusMinimumComponentSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    ...identity,
    kind: z.literal("safety_stock"),
    providerId: z.literal("base_safety_stock_v1"),
  }),
]);
export const surplusFloorSchema = z.strictObject({
  ...identity,
  kind: z.literal("override_floor"),
  providerId: z.literal("active_min_floor_v1"),
});
export const surplusDeductionComponentSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    ...identity,
    kind: z.literal("existing_commitment_bundle"),
    providerId: z.literal("existing_commitments_v1"),
  }),
  z.strictObject({
    ...identity,
    kind: z.literal("snapshot_reservations"),
    providerId: z.literal("snapshot_reservations_v1"),
  }),
  z.strictObject({
    ...identity,
    kind: z.literal("open_sales_order_demand"),
    providerId: z.literal("open_sales_order_demand_v1"),
  }),
  z.strictObject({
    ...identity,
    kind: z.literal("staged_unacknowledged_transfers"),
    providerId: z.literal("staged_unacknowledged_transfers_v1"),
  }),
]);
export const surplusRetentionComponentSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    ...identity,
    kind: z.literal("historical_customer_sales"),
    providerId: z.literal("historical_customer_sales_v1"),
    lookbackCompleteMonths: z.literal(12),
    coverageMonths: z.number().int().min(1).max(24),
  }),
  z.strictObject({ ...identity, kind: z.literal("composed_minimum") }),
  z.strictObject({
    ...identity,
    kind: z.literal("existing_effective_trigger"),
    providerId: z.literal("existing_effective_trigger_v1"),
  }),
]);
/** Existing engine contract only: SO demand excludes reserved stock; staged transfers await acknowledgement. */
const compatibleDeductionPairs = new Set([
  "open_sales_order_demand|snapshot_reservations",
  "snapshot_reservations|staged_unacknowledged_transfers",
  "open_sales_order_demand|staged_unacknowledged_transfers",
]);
const surplusDefinitionShapeSchema = z.strictObject({
  schemaVersion: z.literal(1),
  minimum: z.strictObject({
    base: z.strictObject({
      components: z.array(surplusMinimumComponentSchema).max(1),
      combine: z.literal("maximum"),
    }),
    floor: surplusFloorSchema.optional(),
  }),
  surplus: z.strictObject({
    stockBasis: z.literal("physical_on_hand"),
    deductions: z.array(surplusDeductionComponentSchema).max(4),
    retention: z.array(surplusRetentionComponentSchema).max(3),
    retentionCombine: z.literal("maximum"),
    result: z.literal("positive_available_less_retention"),
  }),
});
type SurplusDefinitionShape = z.infer<typeof surplusDefinitionShapeSchema>;
type ComponentIdentity = { id: string; kind: string };
function validateComponentIds(
  definition: SurplusDefinitionShape,
  ctx: z.RefinementCtx,
): void {
  const all = [
    ...definition.minimum.base.components,
    ...(definition.minimum.floor ? [definition.minimum.floor] : []),
    ...definition.surplus.deductions,
    ...definition.surplus.retention,
  ];
  if (new Set(all.map((component) => component.id)).size !== all.length)
    ctx.addIssue({ code: "custom", message: "Component IDs must be unique." });
}
function validateComponentKinds(
  group: ComponentIdentity[],
  ctx: z.RefinementCtx,
): void {
  if (new Set(group.map((component) => component.kind)).size !== group.length)
    ctx.addIssue({
      code: "custom",
      message: "Each component kind can be selected once.",
    });
}
function validateCommitmentBundle(
  deductions: ComponentIdentity[],
  ctx: z.RefinementCtx,
): void {
  if (
    deductions.length > 1 &&
    deductions.some(
      (component) => component.kind === "existing_commitment_bundle",
    )
  )
    ctx.addIssue({
      code: "custom",
      message:
        "Existing commitments cannot be combined with individual deductions.",
    });
}
function validateDeductionPairs(
  deductions: ComponentIdentity[],
  ctx: z.RefinementCtx,
): void {
  for (const [index, left] of deductions.entries()) {
    for (const right of deductions.slice(index + 1)) {
      if (
        !compatibleDeductionPairs.has([left.kind, right.kind].sort().join("|"))
      )
        ctx.addIssue({
          code: "custom",
          message: "Deduction provider pair has no compatibility contract.",
        });
    }
  }
}
function validateRetentionAlternatives(
  retention: ComponentIdentity[],
  ctx: z.RefinementCtx,
): void {
  const kinds = retention.map((component) => component.kind);
  if (
    kinds.includes("composed_minimum") &&
    kinds.includes("existing_effective_trigger")
  )
    ctx.addIssue({
      code: "custom",
      message: "Choose composed minimum or the existing effective trigger.",
    });
}
export const surplusDefinitionSchema = surplusDefinitionShapeSchema.superRefine(
  (definition, ctx) => {
    validateComponentIds(definition, ctx);
    for (const group of [
      definition.minimum.base.components,
      definition.surplus.deductions,
      definition.surplus.retention,
    ])
      validateComponentKinds(group, ctx);
    validateCommitmentBundle(definition.surplus.deductions, ctx);
    validateDeductionPairs(definition.surplus.deductions, ctx);
    validateRetentionAlternatives(definition.surplus.retention, ctx);
  },
);
export type SurplusDefinition = z.infer<typeof surplusDefinitionSchema>;
export type SurplusComponent =
  | z.infer<typeof surplusMinimumComponentSchema>
  | z.infer<typeof surplusFloorSchema>
  | z.infer<typeof surplusDeductionComponentSchema>
  | z.infer<typeof surplusRetentionComponentSchema>;
export const surplusProfileSchema = z.strictObject({
  profileId: z.uuid(),
  revision: z.number().int().positive(),
  contentFingerprint: z.string().min(1),
  definition: surplusDefinitionSchema,
  updatedAt: z.iso.datetime(),
  updatedBy: z.uuid(),
});
export type SurplusProfile = z.infer<typeof surplusProfileSchema>;
export const surplusExpectedStateSchema = z.discriminatedUnion("state", [
  z.strictObject({ state: z.literal("absent") }),
  z.strictObject({
    state: z.literal("saved"),
    profileId: z.uuid(),
    revision: z.number().int().positive(),
    contentFingerprint: z.string().min(1),
  }),
]);
export type SurplusExpectedState = z.infer<typeof surplusExpectedStateSchema>;
export const DEFAULT_SURPLUS_DEFINITION: SurplusDefinition = {
  schemaVersion: 1,
  minimum: { base: { components: [], combine: "maximum" } },
  surplus: {
    stockBasis: "physical_on_hand",
    deductions: [
      {
        id: "commitments",
        kind: "existing_commitment_bundle",
        providerId: "existing_commitments_v1",
      },
    ],
    retention: [
      {
        id: "sales",
        kind: "historical_customer_sales",
        providerId: "historical_customer_sales_v1",
        lookbackCompleteMonths: 12,
        coverageMonths: 12,
      },
      {
        id: "trigger",
        kind: "existing_effective_trigger",
        providerId: "existing_effective_trigger_v1",
      },
    ],
    retentionCombine: "maximum",
    result: "positive_available_less_retention",
  },
};
export const surplusProviderSchema = z.object({
  kind: z.string(),
  providerId: z.string().nullable(),
  group: z.enum(["minimum", "floor", "deduction", "retention"]),
  supported: z.boolean(),
  label: z.string(),
  description: z.string(),
  incompatibleKinds: z.array(z.string()),
});
export type SurplusProvider = z.infer<typeof surplusProviderSchema>;
export const SURPLUS_PROVIDERS: SurplusProvider[] = [
  {
    kind: "safety_stock",
    providerId: "base_safety_stock_v1",
    group: "minimum",
    supported: true,
    label: "Base forecast safety stock",
    description:
      "Base safety stock before human overrides. Comparison units must be verified.",
    incompatibleKinds: [],
  },
  {
    kind: "calculated_minimum",
    providerId: null,
    group: "minimum",
    supported: false,
    label: "Calculated minimum",
    description:
      "No authoritative calculated-minimum provider is established. Legacy minimum and reorder point are not substituted.",
    incompatibleKinds: [],
  },
  {
    kind: "override_floor",
    providerId: "active_min_floor_v1",
    group: "floor",
    supported: true,
    label: "Active minimum floor",
    description:
      "Maximum with the active minFloor override; confirmed absence contributes configured zero.",
    incompatibleKinds: [],
  },
  {
    kind: "existing_commitment_bundle",
    providerId: "existing_commitments_v1",
    group: "deduction",
    supported: true,
    label: "Existing commitments (compatibility)",
    description:
      "Existing engine reservation, SO and staged-transfer arithmetic. Existing age, partial-shipment and acknowledgement limitations apply.",
    incompatibleKinds: [
      "snapshot_reservations",
      "open_sales_order_demand",
      "staged_unacknowledged_transfers",
    ],
  },
  ...(
    [
      "snapshot_reservations",
      "open_sales_order_demand",
      "staged_unacknowledged_transfers",
    ] as const
  ).map((kind) => ({
    kind,
    providerId: `${kind}_v1`,
    group: "deduction" as const,
    supported: true,
    label:
      kind === "snapshot_reservations"
        ? "Snapshot reservations"
        : kind === "open_sales_order_demand"
          ? "Open sales order demand"
          : "Staged unacknowledged transfers",
    description:
      "Existing engine source semantics and limitations; individual components combined under the v1 compatibility arithmetic, not a proven booking partition.",
    incompatibleKinds: ["existing_commitment_bundle"],
  })),
  ...(["dated_bookings", "undated_reservations"] as const).map((kind) => ({
    kind,
    providerId: null,
    group: "deduction" as const,
    supported: false,
    label:
      kind === "dated_bookings" ? "Dated bookings" : "Undated reservations",
    description:
      "Producer date, blank-date classification, remaining quantity and overlap evidence are not established.",
    incompatibleKinds: [],
  })),
  {
    kind: "historical_customer_sales",
    providerId: "historical_customer_sales_v1",
    group: "retention",
    supported: true,
    label: "Customer sales coverage",
    description:
      "12 complete-month observation window; 1–24-month protection target. Covered periods and comparable units are required.",
    incompatibleKinds: [],
  },
  {
    kind: "composed_minimum",
    providerId: null,
    group: "retention",
    supported: true,
    label: "Composed minimum",
    description: "Minimum from this same settings revision.",
    incompatibleKinds: ["existing_effective_trigger"],
  },
  {
    kind: "existing_effective_trigger",
    providerId: "existing_effective_trigger_v1",
    group: "retention",
    supported: true,
    label: "Existing effective trigger (compatibility)",
    description:
      "Existing resolved trigger, including applicable overrides and floor.",
    incompatibleKinds: ["composed_minimum"],
  },
];
export function surplusDefinitionWarnings(
  definition: SurplusDefinition,
): string[] {
  return [
    ...(definition.surplus.deductions.length === 0
      ? ["No deductions selected; commitments are not protected."]
      : []),
    ...(definition.surplus.retention.length === 0
      ? ["No retained protection selected."]
      : []),
    ...(definition.minimum.base.components.length === 0 &&
    !definition.minimum.floor
      ? ["No minimum protection selected; minimum is configured zero."]
      : []),
    ...(definition.surplus.deductions.length > 0 &&
    definition.surplus.retention.some(
      (c) => c.kind === "historical_customer_sales",
    )
      ? [
          "Deductions and sales coverage may conservatively protect the same future demand.",
        ]
      : []),
  ];
}
export const surplusComponentTraceSchema = z.object({
  id: z.string(),
  kind: z.string(),
  group: z.enum(["minimum", "floor", "deduction", "retention"]),
  providerId: z.string().nullable(),
  providerVersion: z.literal(1),
  grain: z.object({
    productV3Key: z.string().nullable(),
    warehouseId: z.string(),
  }),
  conversion: z.null(),
  state: z.enum([
    "available",
    "estimated",
    "unavailable",
    "not_selected",
    "not_evaluated",
    "configured_zero",
  ]),
  quantity: z.number().nonnegative().nullable(),
  reason: z.string().nullable(),
  source: z.string().nullable(),
  sourceAsOf: z.string().nullable(),
  unit: z.string().nullable(),
  dominant: z.boolean(),
  parameters: z.record(z.string(), z.union([z.string(), z.number()])),
  dependencies: z.array(z.string()),
});
export type SurplusComponentTrace = z.infer<typeof surplusComponentTraceSchema>;
export const surplusExplanationSchema = z.object({
  minimum: z.number().nullable(),
  minimumStatus: z.enum([
    "calculated",
    "estimated",
    "unavailable",
    "not_evaluated",
  ]),
  deductions: z.number().nullable(),
  retained: z.number().nullable(),
  quantityBasis: z.enum([
    "verified_comparison_unit",
    "native_stock_unit_unspecified",
    "unverified_comparison_unit",
  ]),
  components: z.array(surplusComponentTraceSchema),
  formula: z.string(),
  warnings: z.array(z.string()),
});
export const surplusSettingsIdentitySchema = z.object({
  source: z.enum(["saved", "default", "draft"]),
  profileId: z.uuid().nullable(),
  revision: z.number().int().positive().nullable(),
  contentFingerprint: z.string().nullable(),
  evaluatorVersion: z.literal(2),
  definition: surplusDefinitionSchema,
});
export type SurplusSettingsIdentity = z.infer<
  typeof surplusSettingsIdentitySchema
>;
